<template>
  <div class="view-root" style="display:flex;flex-direction:column;height:100%;overflow:hidden;">
    <!-- Header -->
    <div class="view-header" style="flex-shrink:0;">
      <div>
        <h2 class="view-title">行銷行事曆</h2>
        <p class="view-subtitle">{{ calTitle }} — 已排程活動總覽</p>
      </div>
      <div class="flex items-center gap-2">
        <button class="btn btn-secondary btn-sm" @click="prevMonth">‹ 上月</button>
        <button class="btn btn-secondary btn-sm" @click="goToday">今天</button>
        <button class="btn btn-secondary btn-sm" @click="nextMonth">下月 ›</button>
        <button class="btn btn-primary btn-sm" @click="openCreate(null)">+ 新增活動</button>
      </div>
    </div>

    <div class="flex flex-1 overflow-hidden">
      <!-- Calendar grid -->
      <div class="flex-1 overflow-auto p-4">
        <div v-if="loading" class="loading-state">載入中…</div>
        <div v-else>
          <!-- Weekday labels -->
          <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:4px;margin-bottom:4px;">
            <div v-for="d in ['日','一','二','三','四','五','六']" :key="d"
                 style="text-align:center;font-size:.75rem;font-weight:700;color:var(--gray-400);padding:4px 0;">{{ d }}</div>
          </div>
          <!-- Day cells -->
          <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:4px;">
            <div v-for="cell in calCells" :key="cell.key"
                 @click="cell.inMonth && openCreate(cell.date)"
                 :style="{
                   minHeight:'90px', borderRadius:'8px', padding:'6px',
                   border: cell.isToday ? '2px solid var(--brand-500)' : '1px solid var(--border)',
                   background: cell.inMonth ? 'var(--surface)' : 'var(--gray-50)',
                   cursor: cell.inMonth ? 'pointer' : 'default',
                   opacity: cell.inMonth ? 1 : 0.4,
                 }">
              <div style="font-size:.75rem;font-weight:700;margin-bottom:4px;"
                   :style="{ color: cell.isToday ? 'var(--brand-500)' : cell.inMonth ? 'var(--gray-700)' : 'var(--gray-300)' }">
                {{ cell.day }}
              </div>
              <div v-for="c in cell.campaigns" :key="c.id"
                   @click.stop="openDetail(c)"
                   :style="{
                     fontSize:'.7rem', fontWeight:600, padding:'2px 5px', borderRadius:'4px', marginBottom:'2px',
                     background: statusColor(c.status).bg, color: statusColor(c.status).text,
                     cursor:'pointer', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis',
                   }"
                   :title="c.subject || c.name">
                {{ c.subject || c.name || '（無主旨）' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detail drawer -->
      <transition name="slide-right">
        <div v-if="detailCampaign" style="width:320px;border-left:1px solid var(--border);background:var(--surface);overflow-y:auto;flex-shrink:0;">
          <div style="padding:16px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;">
            <div style="font-weight:700;font-size:.95rem;">活動詳情</div>
            <button class="btn btn-ghost btn-xs" @click="detailCampaign=null">✕</button>
          </div>
          <div style="padding:16px;space-y:12px;" class="space-y-3">
            <div>
              <div style="font-size:.7rem;font-weight:700;color:var(--gray-400);text-transform:uppercase;margin-bottom:2px;">主旨</div>
              <div style="font-size:.9rem;font-weight:600;color:var(--gray-800);">{{ detailCampaign.subject || '（無主旨）' }}</div>
            </div>
            <div>
              <div style="font-size:.7rem;font-weight:700;color:var(--gray-400);text-transform:uppercase;margin-bottom:2px;">寄件者</div>
              <div style="font-size:.85rem;color:var(--gray-600);">{{ detailCampaign.sender }}</div>
            </div>
            <div>
              <div style="font-size:.7rem;font-weight:700;color:var(--gray-400);text-transform:uppercase;margin-bottom:2px;">狀態</div>
              <span :style="{
                display:'inline-block', padding:'2px 8px', borderRadius:'6px', fontSize:'.8rem', fontWeight:700,
                background: statusColor(detailCampaign.status).bg, color: statusColor(detailCampaign.status).text,
              }">{{ statusLabel(detailCampaign.status) }}</span>
            </div>
            <div v-if="detailCampaign.scheduled_at">
              <div style="font-size:.7rem;font-weight:700;color:var(--gray-400);text-transform:uppercase;margin-bottom:2px;">排程時間</div>
              <div style="font-size:.85rem;color:var(--gray-600);">{{ fmtDatetime(detailCampaign.scheduled_at) }}</div>
            </div>
            <div v-if="detailCampaign.created_at">
              <div style="font-size:.7rem;font-weight:700;color:var(--gray-400);text-transform:uppercase;margin-bottom:2px;">建立時間</div>
              <div style="font-size:.85rem;color:var(--gray-600);">{{ fmtDatetime(detailCampaign.created_at) }}</div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Quick Create Modal -->
    <div v-if="showCreate" class="modal-overlay" @click.self="showCreate=false">
      <div class="modal-box" style="max-width:520px;">
        <div class="modal-header">
          <h3>{{ createForm.date ? fmtDate(createForm.date) + ' — ' : '' }}新增行銷活動</h3>
          <button class="modal-close" @click="showCreate=false">✕</button>
        </div>
        <div class="modal-body space-y-3">
          <div>
            <label class="label">主旨 *</label>
            <input v-model="createForm.subject" class="input" placeholder="郵件主旨">
          </div>
          <div>
            <label class="label">寄件者名稱</label>
            <input v-model="createForm.name" class="input" placeholder="例：行銷活動">
          </div>
          <div>
            <label class="label">範本</label>
            <select v-model="createForm.template_id" class="input">
              <option :value="null">— 不使用範本 —</option>
              <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div>
            <label class="label">聯絡人群組</label>
            <select v-model="createForm.group_id" class="input">
              <option :value="null">— 全部聯絡人 —</option>
              <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
            </select>
          </div>
          <div>
            <label class="label">排程時間</label>
            <input v-model="createForm.scheduled_at" type="datetime-local" class="input">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showCreate=false">取消</button>
          <button class="btn btn-primary" :disabled="creating" @click="submitCreate">
            <span v-if="creating" class="spinner spinner-sm"></span>
            {{ creating ? '建立中…' : '建立活動' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ADMIN_API } from '../store/app.js'
import axios from 'axios'

const loading = ref(false)
const campaigns = ref([])
const templates = ref([])
const groups = ref([])
const detailCampaign = ref(null)
const showCreate = ref(false)
const creating = ref(false)

const today = new Date()
const viewYear = ref(today.getFullYear())
const viewMonth = ref(today.getMonth()) // 0-indexed

const createForm = ref({ subject: '', name: '', template_id: null, group_id: null, scheduled_at: '', date: null })

const calTitle = computed(() => {
  const d = new Date(viewYear.value, viewMonth.value, 1)
  return d.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long' })
})

function authHdr() {
  const t = localStorage.getItem('token')
  return t ? { Authorization: `Bearer ${t}` } : {}
}

function prevMonth() {
  if (viewMonth.value === 0) { viewYear.value--; viewMonth.value = 11 }
  else viewMonth.value--
}
function nextMonth() {
  if (viewMonth.value === 11) { viewYear.value++; viewMonth.value = 0 }
  else viewMonth.value++
}
function goToday() {
  viewYear.value = today.getFullYear()
  viewMonth.value = today.getMonth()
}

const calCells = computed(() => {
  const year = viewYear.value
  const month = viewMonth.value
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const todayStr = today.toISOString().slice(0, 10)

  const cells = []
  // Fill leading blanks from prev month
  const prevDays = new Date(year, month, 0).getDate()
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = new Date(year, month - 1, prevDays - i)
    cells.push({ key: `p${i}`, day: prevDays - i, date: d, inMonth: false, isToday: false, campaigns: [] })
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d)
    const dateStr = date.toISOString().slice(0, 10)
    const dayCampaigns = campaigns.value.filter(c => {
      const ts = c.scheduled_at || c.created_at
      if (!ts) return false
      return ts.slice(0, 10) === dateStr
    })
    cells.push({ key: `c${d}`, day: d, date, inMonth: true, isToday: dateStr === todayStr, campaigns: dayCampaigns })
  }
  // Fill trailing blanks
  const total = cells.length
  const trailing = total % 7 === 0 ? 0 : 7 - (total % 7)
  for (let i = 1; i <= trailing; i++) {
    const d = new Date(year, month + 1, i)
    cells.push({ key: `n${i}`, day: i, date: d, inMonth: false, isToday: false, campaigns: [] })
  }
  return cells
})

function statusColor(status) {
  const map = {
    sent: { bg: '#dcfce7', text: '#166534' },
    scheduled: { bg: '#dbeafe', text: '#1e40af' },
    draft: { bg: '#f3f4f6', text: '#374151' },
    failed: { bg: '#fee2e2', text: '#991b1b' },
    sending: { bg: '#fef9c3', text: '#854d0e' },
  }
  return map[status] || { bg: '#f3f4f6', text: '#374151' }
}
function statusLabel(s) {
  const map = { sent: '已發送', scheduled: '已排程', draft: '草稿', failed: '失敗', sending: '發送中' }
  return map[s] || s || '未知'
}
function fmtDatetime(s) {
  if (!s) return '—'
  try { return new Date(s).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) } catch { return s }
}
function fmtDate(d) {
  if (!d) return ''
  try { return d.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric' }) } catch { return '' }
}

function openDetail(c) {
  detailCampaign.value = c
}

function openCreate(date) {
  const dt = date || new Date()
  const pad = n => String(n).padStart(2, '0')
  const local = `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T12:00`
  createForm.value = { subject: '', name: '', template_id: null, group_id: null, scheduled_at: local, date }
  detailCampaign.value = null
  showCreate.value = true
}

async function submitCreate() {
  if (!createForm.value.subject.trim()) {
    window.$toast?.error('請填寫主旨')
    return
  }
  creating.value = true
  try {
    const payload = {
      subject: createForm.value.subject,
      name: createForm.value.name || createForm.value.subject,
      template_id: createForm.value.template_id || undefined,
      group_id: createForm.value.group_id || undefined,
      scheduled_at: createForm.value.scheduled_at ? new Date(createForm.value.scheduled_at).toISOString() : undefined,
      status: 'scheduled',
    }
    await axios.post(`${ADMIN_API}/ai/marketing/campaigns`, payload, { headers: authHdr() })
    window.$toast?.success('活動已建立')
    showCreate.value = false
    await loadCampaigns()
  } catch (e) {
    window.$toast?.error('建立失敗', e?.response?.data?.detail || e.message)
  } finally {
    creating.value = false
  }
}

async function loadCampaigns() {
  loading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/marketing/campaigns`, { headers: authHdr(), params: { limit: 200 } })
    campaigns.value = Array.isArray(res.data) ? res.data : (res.data?.items || [])
  } catch (e) {
    console.error('load campaigns', e)
  } finally {
    loading.value = false
  }
}

async function loadTemplates() {
  try {
    const res = await axios.get(`${ADMIN_API}/marketing/templates`, { headers: authHdr() })
    templates.value = Array.isArray(res.data) ? res.data : (res.data?.items || [])
  } catch {}
}

async function loadGroups() {
  try {
    const res = await axios.get(`${ADMIN_API}/contacts/groups`, { headers: authHdr() })
    groups.value = Array.isArray(res.data) ? res.data : []
  } catch {}
}

onMounted(() => {
  loadCampaigns()
  loadTemplates()
  loadGroups()
})
</script>

<style scoped>
.slide-right-enter-active, .slide-right-leave-active { transition: transform .2s, opacity .2s; }
.slide-right-enter-from, .slide-right-leave-to { transform: translateX(100%); opacity: 0; }
</style>
